import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, erroMsg } from "../../lib/api";
import { useMeta } from "../../context/MetaContext";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

export default function AdminNcms() {
  const { recarregar } = useMeta();
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ codigo: "", descricao: "" });
  const [salvando, setSalvando] = useState(false);
  const [excluir, setExcluir] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get("/admin/ncms");
      setItens(r.data.itens);
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvar = async () => {
    if (!/^\d{4}\.?\d{2}\.?\d{2}$/.test(form.codigo.trim())) {
      toast.error("Informe o NCM no formato 0000.00.00");
      return;
    }
    setSalvando(true);
    try {
      if (editando) {
        await api.put(`/admin/ncms/${editando.codigo}`, form);
        toast.success("NCM atualizado");
      } else {
        await api.post("/admin/ncms", form);
        toast.success("NCM criado com matriz de alíquotas zerada");
      }
      setAberto(false);
      carregar();
      recarregar();
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <AdminShell
      titulo="NCMs"
      descricao="Cada NCM possui sua própria matriz de alíquotas de ST/DIFAL por estado e enquadramento."
      acoes={
        <Button
          onClick={() => {
            setEditando(null);
            setForm({ codigo: "", descricao: "" });
            setAberto(true);
          }}
          data-testid="admin-ncms-novo-button"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Novo NCM
        </Button>
      }
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table data-testid="admin-ncms-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">Código</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-[110px] text-right">Produtos</TableHead>
              <TableHead className="w-[110px] text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" />
                </TableCell>
              </TableRow>
            ) : (
              itens.map((n) => (
                <TableRow key={n.codigo} data-testid={`admin-ncm-item-${n.codigo}`}>
                  <TableCell className="font-mono-num font-medium">{n.codigo}</TableCell>
                  <TableCell className="max-w-[680px] text-[12.5px] text-slate-600">
                    {n.descricao || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary">{n.produtos}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditando(n);
                          setForm({ codigo: n.codigo, descricao: n.descricao || "" });
                          setAberto(true);
                        }}
                        data-testid={`admin-ncm-editar-${n.codigo}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-destructive"
                        onClick={() => setExcluir(n)}
                        data-testid={`admin-ncm-excluir-${n.codigo}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Sheet open={aberto} onOpenChange={setAberto}>
        <SheetContent className="bg-white sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{editando ? "Editar NCM" : "Novo NCM"}</SheetTitle>
            <SheetDescription>
              Ao criar um NCM, a matriz de alíquotas é gerada zerada para os 27 estados.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label>Código NCM</Label>
              <Input
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                disabled={!!editando}
                placeholder="3917.21.00"
                className="h-10 font-mono-num"
                data-testid="admin-ncm-codigo"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Descrição</Label>
              <Textarea
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                rows={6}
                data-testid="admin-ncms-descricao-textarea"
              />
              <div className="text-right text-[11px] text-slate-400">
                {form.descricao.length} caracteres
              </div>
            </div>
            <div className="flex gap-2">
              <Button className="h-11 flex-1" onClick={salvar} disabled={salvando} data-testid="admin-ncm-salvar">
                {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editando ? "Salvar" : "Criar NCM"}
              </Button>
              <Button variant="outline" className="h-11" onClick={() => setAberto(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!excluir} onOpenChange={(o) => !o && setExcluir(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir NCM {excluir?.codigo}?</AlertDialogTitle>
            <AlertDialogDescription>
              A matriz de alíquotas deste NCM também será removida. NCMs em uso por produtos não
              podem ser excluídos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                try {
                  await api.delete(`/admin/ncms/${excluir.codigo}`);
                  toast.success("NCM excluído");
                  setExcluir(null);
                  carregar();
                  recarregar();
                } catch (e) {
                  toast.error(erroMsg(e));
                }
              }}
              data-testid="admin-ncm-excluir-confirmar"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
